import React from "react"
import { Grid, InputAdornment, MenuItem, Select, TextField } from "@material-ui/core"

import { ChatBotFlowBlockRuleValidation, ChatBotFlowBlockRuleValidationType } from "@/protocols/chatBot"
import { AvailableValidationInputValue } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/GenericConditionSectionEditor"
import SelectInput from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SelectInput"

import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"
import { buildClientBoardContactValueVariable } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowVariable"

import { sanitizeMoneyInputValue } from "@/utils/currencyFormatter"

const CENTS_IN_ONE_REAL = 100

const CONTACT_VALUE_VALIDATION_TYPE: ChatBotFlowBlockRuleValidationType = "deep-validation"
const OPPORTUNITY_STATUS_VALIDATION_TYPE: ChatBotFlowBlockRuleValidationType = "board-opportunity-status"
const LOSS_REASON_VALIDATION_TYPE: ChatBotFlowBlockRuleValidationType = "board-loss-reason"

export const VALIDATION_TYPE_BOARD_CONDITION_TO_NAME: Partial<Record<ChatBotFlowBlockRuleValidationType, string>> = {
	"array-contains": "Pertence à etapa",
	"not-contains": "Não pertence à etapa",
	[CONTACT_VALUE_VALIDATION_TYPE]: "Valor do contato",
	[OPPORTUNITY_STATUS_VALIDATION_TYPE]: "Status do contato",
	[LOSS_REASON_VALIDATION_TYPE]: "Motivo da perda"
}

type ContactValueOperator = "less_than" | "greater_than" | "between" | "equal"

export const contactValueOperatorToLabel: Record<ContactValueOperator, string> = {
	less_than: "Menor que",
	greater_than: "Maior que",
	between: "Entre",
	equal: "Igual"
}

const contactValueOperators = Object.keys(contactValueOperatorToLabel) as ContactValueOperator[]

const contactValueOperatorToValidationType: Partial<Record<ContactValueOperator, ChatBotFlowBlockRuleValidationType>> = {
	less_than: "lower-than",
	greater_than: "greater-than",
	equal: "equals"
}

const validationTypeToContactValueOperator: Partial<Record<ChatBotFlowBlockRuleValidationType, ContactValueOperator>> = {
	"lower-than": "less_than",
	"greater-than": "greater_than",
	equals: "equal"
}

export type OpportunityStatusOperator = "equal" | "not_equal"

const opportunityStatusOperatorToLabel: Record<OpportunityStatusOperator, string> = {
	equal: "Igual",
	not_equal: "Não igual"
}

const opportunityStatusOperators = Object.keys(opportunityStatusOperatorToLabel) as OpportunityStatusOperator[]

export type OpportunityStatusValue = "won" | "lost"

const opportunityStatusValueToLabel: Record<OpportunityStatusValue, string> = {
	won: "Ganho",
	lost: "Perdido"
}

const opportunityStatusValues = Object.keys(opportunityStatusValueToLabel) as OpportunityStatusValue[]

const LOSS_REASON_OTHER_ID = 0

export const isContactValueValidation = (validation: ChatBotFlowBlockRuleValidation): boolean => {
	return validation.type === CONTACT_VALUE_VALIDATION_TYPE && Boolean(validation.extra_data?.client_board_contact_value_condition)
}

export const isOpportunityStatusValidation = (validation: ChatBotFlowBlockRuleValidation): boolean => {
	return validation.type === OPPORTUNITY_STATUS_VALIDATION_TYPE
}

export const isLossReasonValidation = (validation: ChatBotFlowBlockRuleValidation): boolean => {
	return validation.type === LOSS_REASON_VALIDATION_TYPE
}

export const getLossReasonId = (validation: ChatBotFlowBlockRuleValidation): number | undefined => {
	return validation.extra_data?.client_board_loss_reason_condition?.lossReasonId
}

export const getOpportunityStatusOperator = (validation: ChatBotFlowBlockRuleValidation): OpportunityStatusOperator => {
	return validation.extra_data?.client_board_opportunity_status_condition?.operator ?? "equal"
}

export const getOpportunityStatusValue = (validation: ChatBotFlowBlockRuleValidation): OpportunityStatusValue => {
	return validation.extra_data?.client_board_opportunity_status_condition?.status ?? "won"
}

export const getOpportunityStatusLossReasonId = (validation: ChatBotFlowBlockRuleValidation): number | undefined => {
	return validation.extra_data?.client_board_opportunity_status_condition?.lossReasonId
}

export const getContactValueOperator = (validation: ChatBotFlowBlockRuleValidation): ContactValueOperator => {
	if (!isContactValueValidation(validation)) {
		return "less_than"
	}

	const hasTwoNestedValidations = (validation.deep_validations?.length ?? 0) >= 2

	if (hasTwoNestedValidations) {
		return "between"
	}

	const nestedType = validation.deep_validations?.[0]?.type

	return validationTypeToContactValueOperator[nestedType] ?? "less_than"
}

const formatRealsFromCents = (valueInCents?: number | string): string => {
	const hasValueDefined = valueInCents !== undefined && valueInCents !== null && valueInCents !== ""

	if (!hasValueDefined) {
		return ""
	}

	return (Number(valueInCents) / CENTS_IN_ONE_REAL).toString().replace(".", ",")
}

const parseCentsFromReals = (valueInReals: string): number | "" => {
	if (valueInReals.trim() === "") {
		return ""
	}

	return Math.round(Number(valueInReals.replace(",", ".")) * CENTS_IN_ONE_REAL)
}

const buildContactValueDeepValidations = (
	operator: ContactValueOperator,
	boardId: number | null | undefined,
	fromValueInCents: number | "" | undefined,
	toValueInCents: number | "" | undefined
): ChatBotFlowBlockRuleValidation[] => {
	const contactValueVariable = boardId ? buildClientBoardContactValueVariable(boardId) : ""

	if (operator === "between") {
		return [
			{
				type: "greater-than-or-equal",
				first_param: contactValueVariable,
				second_param: fromValueInCents ?? ""
			},
			{
				type: "lower-than-or-equal",
				first_param: contactValueVariable,
				second_param: toValueInCents ?? ""
			}
		]
	}

	return [
		{
			type: contactValueOperatorToValidationType[operator],
			first_param: contactValueVariable,
			second_param: fromValueInCents ?? ""
		}
	]
}

const buildLossReasonOptions = (board: { lossReasons?: Array<{ id: number, reasonText: string }>, lossOtherEnabled?: boolean } | undefined) => {
	const configuredReasons = board?.lossReasons ?? []

	if (board?.lossOtherEnabled) {
		return [...configuredReasons, { id: LOSS_REASON_OTHER_ID, reasonText: "Outro" }]
	}

	return configuredReasons
}

const ClientBoardStageInput: AvailableValidationInputValue = {
	title: "Funil",
	defaultFirstParamValue: "{{clientBoardStageIds}}",
	firstParam: {
		render: ({ validation, onChangeValidation, constructionResources }) => {
			const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()

			const boardIdSelected = validation?.extra_data?.board_stage_condition?.board_id
			const boardSelected = constructionResources.conditionOptions.boards.find((board) => board.id === Number(boardIdSelected))

			const handleChangeBoard = (board?: { id: number } | null) => {
				const newBoardId = board?.id || null

				const additionalChanges: Partial<ChatBotFlowBlockRuleValidation> = {}

				if (isContactValueValidation(validation)) {
					const operator = getContactValueOperator(validation)

					additionalChanges.deep_validations = buildContactValueDeepValidations(
						operator,
						newBoardId,
						validation.deep_validations?.[0]?.second_param as number | "",
						validation.deep_validations?.[1]?.second_param as number | ""
					)
					additionalChanges.deep_validation_logic = "and"
				}

				onChangeValidation({
					extra_data: {
						board_stage_condition: {
							board_id: newBoardId
						}
					},
					...additionalChanges
				})
			}

			return (
				<SelectInput
					value={boardSelected}
					onChange={handleChangeBoard}
					options={constructionResources.conditionOptions.boards}
					getOptionSelected={(option, value) => option.id === value?.id}
					placeholder="Selecione o funil"
					onUpdate={async () => {
						await chatBotFlowConstructorStore.loadSpecificConstructionResources("conditionOptions")
					}}
					noOptionsText={"Nenhum funil encontrado"}
				/>
			)
		}
	},
	secondParam: {
		render: ({ validation, constructionResources, onChangeValidation, onChangeValidationValue }) => {
			const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()

			const boardIdSelected = validation?.extra_data?.board_stage_condition?.board_id
			const boardSelected = constructionResources.conditionOptions.boards.find((board) => board.id === Number(boardIdSelected))

			const isOpportunityStatus = isOpportunityStatusValidation(validation)
			const isLossReason = isLossReasonValidation(validation)
			const isContactValue = isContactValueValidation(validation)

			if (isOpportunityStatus) {
				const selectedOperator = getOpportunityStatusOperator(validation)

				const handleChangeOperator = (operator: OpportunityStatusOperator) => {
					onChangeValidation({
						extra_data: {
							client_board_opportunity_status_condition: {
								...validation.extra_data?.client_board_opportunity_status_condition,
								operator
							}
						}
					})
				}

				return (
					<Select
						variant="outlined"
						value={selectedOperator}
						onChange={(event) => handleChangeOperator(event.target.value as OpportunityStatusOperator)}
						fullWidth
					>
						{opportunityStatusOperators.map((operator) => (
							<MenuItem key={operator} value={operator}>
								{opportunityStatusOperatorToLabel[operator]}
							</MenuItem>
						))}
					</Select>
				)
			}

			if (isLossReason) {
				const selectedLossReasonId = getLossReasonId(validation)
				const lossReasonOptions = buildLossReasonOptions(boardSelected)

				const handleChangeLossReason = (lossReasonId: number) => {
					onChangeValidation({
						extra_data: {
							client_board_loss_reason_condition: {
								lossReasonId
							}
						}
					})
				}

				return (
					<Select
						variant="outlined"
						value={selectedLossReasonId ?? ""}
						onChange={({ target }) => handleChangeLossReason(target.value as number)}
						fullWidth
						displayEmpty
					>
						{lossReasonOptions.map((reason) => (
							<MenuItem key={reason.id} value={reason.id}>
								{reason.reasonText}
							</MenuItem>
						))}
					</Select>
				)
			}

			if (isContactValue) {
				const selectedOperator = getContactValueOperator(validation)

				const handleChangeOperator = (operator: ContactValueOperator) => {
					onChangeValidation({
						deep_validations: buildContactValueDeepValidations(
							operator,
							boardIdSelected,
							validation.deep_validations?.[0]?.second_param as number | "",
							operator === "between" ? (validation.deep_validations?.[1]?.second_param as number | "") : undefined
						),
						deep_validation_logic: "and"
					})
				}

				return (
					<Select
						variant="outlined"
						value={selectedOperator}
						onChange={(event) => handleChangeOperator(event.target.value as ContactValueOperator)}
						fullWidth
					>
						{contactValueOperators.map((operator) => (
							<MenuItem key={operator} value={operator}>
								{contactValueOperatorToLabel[operator]}
							</MenuItem>
						))}
					</Select>
				)
			}

			const stageIdSelected = validation?.second_param
			const stageSelected = boardSelected?.stages.find((stage) => stage.id === stageIdSelected)

			return (
				<SelectInput
					key={boardIdSelected}
					value={stageSelected}
					onChange={stage => onChangeValidationValue(stage?.id ?? "")}
					options={boardSelected?.stages ?? []}
					getOptionSelected={(option, value) => option.id === value?.id}
					placeholder="Etapa"
					onUpdate={async () => {
						await chatBotFlowConstructorStore.loadSpecificConstructionResources("conditionOptions")
					}}
					noOptionsText={"Nenhuma etapa encontrada"}
				/>
			)
		}
	},
	extraContent: {
		render: ({ validation, onChangeValidation }) => {
			const isOpportunityStatus = isOpportunityStatusValidation(validation)
			const isContactValue = isContactValueValidation(validation)

			if (isOpportunityStatus) {
				const selectedStatusValue = getOpportunityStatusValue(validation)

				const handleChangeStatusValue = (status: OpportunityStatusValue) => {
					onChangeValidation({
						extra_data: {
							client_board_opportunity_status_condition: {
								...validation.extra_data?.client_board_opportunity_status_condition,
								status
							}
						}
					})
				}

				return (
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Select
								variant="outlined"
								value={selectedStatusValue}
								onChange={(event) => handleChangeStatusValue(event.target.value as OpportunityStatusValue)}
								fullWidth
							>
								{opportunityStatusValues.map((status) => (
									<MenuItem key={status} value={status}>
										{opportunityStatusValueToLabel[status]}
									</MenuItem>
								))}
							</Select>
						</Grid>
					</Grid>
				)
			}

			if (!isContactValue) {
				return <></>
			}

			const boardIdSelected = validation?.extra_data?.board_stage_condition?.board_id
			const operator = getContactValueOperator(validation)
			const isBetween = operator === "between"

			const fromValueInCents = validation.deep_validations?.[0]?.second_param as number | "" | undefined
			const toValueInCents = validation.deep_validations?.[1]?.second_param as number | "" | undefined

			const fromValueDisplay = validation.extra_data?.client_board_contact_value_condition?.fromValueDisplay ??
				formatRealsFromCents(fromValueInCents)
			const toValueDisplay = validation.extra_data?.client_board_contact_value_condition?.toValueDisplay ??
				formatRealsFromCents(toValueInCents)

			const handleChangeFromValue = (inputValue: string) => {
				const sanitizedValue = sanitizeMoneyInputValue(inputValue)

				onChangeValidation({
					deep_validations: buildContactValueDeepValidations(operator, boardIdSelected, parseCentsFromReals(sanitizedValue), toValueInCents),
					deep_validation_logic: "and",
					extra_data: {
						client_board_contact_value_condition: { fromValueDisplay: sanitizedValue }
					}
				})
			}

			const handleChangeToValue = (inputValue: string) => {
				const sanitizedValue = sanitizeMoneyInputValue(inputValue)

				onChangeValidation({
					deep_validations: buildContactValueDeepValidations(operator, boardIdSelected, fromValueInCents, parseCentsFromReals(sanitizedValue)),
					deep_validation_logic: "and",
					extra_data: {
						client_board_contact_value_condition: { toValueDisplay: sanitizedValue }
					}
				})
			}

			return (
				<Grid container spacing={1}>
					<Grid item xs={isBetween ? 6 : 12}>
						<TextField
							value={fromValueDisplay}
							onChange={(event) => handleChangeFromValue(event.target.value)}
							variant="outlined"
							type="text"
							inputMode="decimal"
							fullWidth
							placeholder={isBetween ? "Mínimo" : "Valor"}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										R$
									</InputAdornment>
								)
							}}
						/>
					</Grid>

					{isBetween && (
						<Grid item xs={6}>
							<TextField
								value={toValueDisplay}
								onChange={(event) => handleChangeToValue(event.target.value)}
								variant="outlined"
								type="text"
								inputMode="decimal"
								fullWidth
								placeholder="Máximo"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											R$
										</InputAdornment>
									)
								}}
							/>
						</Grid>
					)}
				</Grid>
			)
		}
	},
	types: ({ canUseContactValue, canUseBoardOpportunityStatus }) => [
		"array-contains",
		"not-contains",
		...(canUseContactValue ? [CONTACT_VALUE_VALIDATION_TYPE] : []),
		...(canUseBoardOpportunityStatus ? [OPPORTUNITY_STATUS_VALIDATION_TYPE, LOSS_REASON_VALIDATION_TYPE] : [])
	],
	customValidationTypeName: VALIDATION_TYPE_BOARD_CONDITION_TO_NAME,
	onTypeChange: (newType) => {
		const isChangingToContactValue = newType === CONTACT_VALUE_VALIDATION_TYPE
		const isChangingToOpportunityStatus = newType === OPPORTUNITY_STATUS_VALIDATION_TYPE
		const isChangingToLossReason = newType === LOSS_REASON_VALIDATION_TYPE

		return {
			second_param: "",
			deep_validations: isChangingToContactValue ? buildContactValueDeepValidations("less_than", null, "", undefined) : undefined,
			deep_validation_logic: isChangingToContactValue ? "and" : undefined,
			extra_data: {
				client_board_contact_value_condition: isChangingToContactValue ? { fromValueDisplay: null, toValueDisplay: null } : undefined,
				client_board_opportunity_status_condition: isChangingToOpportunityStatus ? { operator: "equal", status: "won" } : undefined,
				client_board_loss_reason_condition: isChangingToLossReason ? { lossReasonId: 0 } : undefined
			}
		}
	}
}

export default ClientBoardStageInput
